import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isSuccess } from '@carvana/merch-result';
import { useContext } from 'react';
import type { ResultWithCorrelationId } from '~/api/middleware/types';
import { httpWithAuth } from '~/clients';
import { SearchApiClientId } from '~/constants/common/ApiClientId';
import { getCreditStatus } from '~/constants/endpoints';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import FinanceContext from '~/context/srp/FinanceContext';
import type { FinancingAdapterUserCreditStatusSummary } from '~/contracts/common';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetCreditStatus() {
  const isLoggedIn = useIsLoggedIn();
  const {
    creditStatus: { setData, setError, setLoaded }
  } = useContext(FinanceContext);
  const eventCorrelationId = useEventCorrelationId();

  useEffectWithController(
    controller => {
      if (!isLoggedIn) {
        setLoaded();

        return;
      }

      httpWithAuth
        .get<ResultWithCorrelationId<FinancingAdapterUserCreditStatusSummary>>(
          getCreditStatus({ currentPageUrl: window.location.href }),
          {
            headers: {
              'x-client-id': SearchApiClientId,
              'x-correlation-id': eventCorrelationId
            },
            signal: controller.signal
          }
        )
        .then(res => {
          setData(isSuccess(res.data) ? res.data.content : {});
        })
        .catch(
          generateErrorLogger(
            'Credit Status',
            { correlationId: eventCorrelationId },
            { callback: setError, controller }
          )
        );
    },
    [eventCorrelationId, isLoggedIn, setData, setError, setLoaded]
  );
}
